import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Tabs,
  Dropdown,
  MenuProps,
  App,
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { TransactionQueueStatus, TransactionQueueFilters, TransactionQueueWithUser } from '@mygate/common';
import { transactionQueueService } from '@/services/transactionQueue';
import { TransactionStats } from '@/components/TransactionStats';
import { TransactionDetailModal } from '@/components/TransactionDetailModal';
import { GenerateTransactionsModal } from '@/components/GenerateTransactionsModal';
import { StandingOrdersTab } from '@/components/StandingOrdersTab';
import { AutoGenerationTab } from '@/components/AutoGenerationTab';
import { ColumnsType } from 'antd/es/table';
import { formatDateTime } from '@/utils/date';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export function TransactionQueuePage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Omit<TransactionQueueFilters, 'userId'>>({});
  const [userIdSearch, setUserIdSearch] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionQueueWithUser | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('queue');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });
  const { modal, message } = App.useApp();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userIdSearch), 300);
    return () => clearTimeout(timer);
  }, [userIdSearch]);

  // Fetch statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['transaction-stats'],
    queryFn: () => transactionQueueService.getStatistics(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch transactions
  const {
    data: transactionsResponse,
    isLoading: transactionsLoading,
    refetch,
  } = useQuery({
    queryKey: ['transactions', filters, pagination],
    queryFn: () => transactionQueueService.fetchTransactionQueue({ ...filters, ...pagination }),
  });

  const transactions = transactionsResponse?.data ?? [];

  // Handle direct transaction ID from URL
  useEffect(() => {
    const transactionId = searchParams.get('id');
    if (transactionId && transactions.length > 0) {
      const transaction = transactions.find(t => t.id === transactionId);
      if (transaction) {
        setSelectedTransaction(transaction);
        setDetailModalVisible(true);
        // Clear the parameter to prevent re-opening on page refresh
        setSearchParams({});
      }
    }
  }, [searchParams, transactions, setSearchParams]);

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: (ids: string[]) => transactionQueueService.bulkApprove({ transactionIds: ids }),
    onSuccess: data => {
      if (!data) return;
      message.success(`${data.approved.length} transactions approved`);
      if (data.failed.length > 0) {
        message.warning(`${data.failed.length} transactions failed to approve`);
      }
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
      setSelectedRowKeys([]);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve transactions';
      message.error(errorMessage);
    },
  });

  // Bulk reject mutation
  const bulkRejectMutation = useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason: string }) =>
      transactionQueueService.bulkReject({ transactionIds: ids, reason }),
    onSuccess: data => {
      if (!data) return;
      message.success(`${data.rejected.length} transactions rejected`);
      if (data.failed.length > 0) {
        message.warning(`${data.failed.length} transactions failed to reject`);
      }
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
      setSelectedRowKeys([]);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reject transactions';
      message.error(errorMessage);
    },
  });

  // Filter transactions based on partial user ID or other fields
  const filteredTransactions = useMemo(() => {
    if (!debouncedSearch) return transactions;

    const searchLower = debouncedSearch.toLowerCase();
    return transactions.filter(transaction => {
      // Match partial userId
      if (transaction.userId?.toLowerCase().includes(searchLower)) return true;

      // Match transaction ID
      if (transaction.id?.toLowerCase().includes(searchLower)) return true;

      // Match recipient name
      if (transaction.recipient?.toLowerCase().includes(searchLower)) return true;

      // Match purpose
      if (transaction.purpose?.toLowerCase().includes(searchLower)) return true;

      // Match IBAN (useful for searching by account)
      if (transaction.iban?.toLowerCase().includes(searchLower)) return true;

      // Match user details
      if (transaction.user?.firstName?.toLowerCase().includes(searchLower)) return true;
      if (transaction.user?.lastName?.toLowerCase().includes(searchLower)) return true;
      if (transaction.user?.phoneNumber?.toLowerCase().includes(searchLower)) return true;
      if (transaction.user?.email?.toLowerCase().includes(searchLower)) return true;

      return false;
    });
  }, [transactions, debouncedSearch]);

  const handleBulkApprove = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select transactions to approve');
      return;
    }

    modal.confirm({
      title: 'Approve Transactions',
      content: `Are you sure you want to approve ${selectedRowKeys.length} transaction(s)?`,
      onOk: () => {
        bulkApproveMutation.mutate(selectedRowKeys);
      },
    });
  };

  const handleBulkReject = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select transactions to reject');
      return;
    }

    let rejectReason = '';
    modal.confirm({
      title: 'Reject Transactions',
      content: (
        <div>
          <p>Are you sure you want to reject {selectedRowKeys.length} transaction(s)?</p>
          <Input
            placeholder="Rejection reason"
            onChange={e => (rejectReason = e.target.value)}
            style={{ marginTop: 10 }}
          />
        </div>
      ),
      onOk: () => {
        if (!rejectReason.trim()) {
          message.error('Please provide a rejection reason');
          return Promise.reject();
        }
        bulkRejectMutation.mutate({ ids: selectedRowKeys, reason: rejectReason });
      },
    });
  };

  const handleExportCSV = () => {
    if (filteredTransactions.length === 0) {
      message.warning('No transactions to export');
      return;
    }

    const filename = `transactions-${dayjs().format('YYYY-MM-DD-HHmmss')}.csv`;
    transactionQueueService.downloadCSV(filteredTransactions, filename);
    message.success(`CSV exported successfully (${filteredTransactions.length} records)`);
  };

  const handleViewDetails = (transaction: TransactionQueueWithUser) => {
    setSelectedTransaction(transaction);
    setDetailModalVisible(true);
  };

  const getStatusColor = (status: TransactionQueueStatus) => {
    switch (status) {
      case TransactionQueueStatus.GENERATED:
        return 'default';
      case TransactionQueueStatus.PENDING_APPROVAL:
        return 'warning';
      case TransactionQueueStatus.APPROVED:
        return 'processing';
      case TransactionQueueStatus.PROCESSING:
        return 'processing';
      case TransactionQueueStatus.COMPLETED:
        return 'success';
      case TransactionQueueStatus.FAILED:
        return 'error';
      case TransactionQueueStatus.CANCELED:
        return 'default';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<TransactionQueueWithUser> = useMemo(
    () => [
      {
        title: 'Auftrag & Kunde',
        key: 'identity',
        width: 160,
        render: (_, record) => (
          <div className="table-cell-stack">
            <Button type="link" className="table-cell-link" onClick={() => handleViewDetails(record)}>
              {record.id.slice(-8)}
            </Button>
            <Typography.Text className="table-cell-muted">
              {[record.user?.firstName, record.user?.lastName].filter(Boolean).join(' ') ||
                record.user?.phoneNumber ||
                record.user?.email ||
                record.userId.slice(-8)}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: TransactionQueueStatus) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      },
      {
        title: 'Due Date',
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: 120,
        render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
        sorter: (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
      },
      {
        title: 'Empfänger & Verwendungszweck',
        key: 'recipient',
        width: 235,
        render: (_, record) => (
          <div className="table-cell-stack">
            <Typography.Text strong>{record.recipient}</Typography.Text>
            <Typography.Text className="table-cell-muted" copyable={{ text: record.iban }}>
              {record.iban}
            </Typography.Text>
            <Typography.Text className="table-cell-muted">{record.purpose || '—'}</Typography.Text>
          </div>
        ),
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
        align: 'right',
        render: (amount: string, record: TransactionQueueWithUser) => {
          return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: record.currency || 'EUR',
          }).format(parseFloat(amount));
        },
        sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
      },
      {
        title: 'Generated At',
        dataIndex: 'generatedAt',
        key: 'generatedAt',
        width: 140,
        render: (date: string) => formatDateTime(date),
        sorter: (a, b) => new Date(a.generatedAt).getTime() - new Date(b.generatedAt).getTime(),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        fixed: 'right',
        render: (_, record) => {
          const items: MenuProps['items'] = [
            {
              key: 'view',
              label: 'View Details',
              onClick: () => handleViewDetails(record),
            },
          ];

          if (record.status === TransactionQueueStatus.PENDING_APPROVAL) {
            items.push(
              {
                key: 'approve',
                label: 'Approve',
                onClick: () => {
                  modal.confirm({
                    title: 'Approve Transaction',
                    content: 'Are you sure you want to approve this transaction?',
                    onOk: async () => {
                      try {
                        await transactionQueueService.approveTransaction(record.id);
                        message.success('Transaction approved');
                        refetch();
                        queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
                      } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : 'Failed to approve transaction';
                        message.error(errorMessage);
                      }
                    },
                  });
                },
              },
              {
                key: 'reject',
                label: 'Reject',
                danger: true,
                onClick: () => {
                  let rejectReason = '';
                  modal.confirm({
                    title: 'Reject Transaction',
                    content: (
                      <div>
                        <p>Are you sure you want to reject this transaction?</p>
                        <Input
                          placeholder="Rejection reason"
                          onChange={e => (rejectReason = e.target.value)}
                          style={{ marginTop: 10 }}
                        />
                      </div>
                    ),
                    onOk: async () => {
                      if (!rejectReason.trim()) {
                        message.error('Please provide a rejection reason');
                        return Promise.reject();
                      }
                      try {
                        await transactionQueueService.rejectTransaction(record.id, rejectReason);
                        message.success('Transaction rejected');
                        refetch();
                        queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
                      } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : 'Failed to reject transaction';
                        message.error(errorMessage);
                      }
                    },
                  });
                },
              },
            );
          }

          if (record.status === TransactionQueueStatus.FAILED) {
            items.push({
              key: 'retry',
              label: 'Retry',
              onClick: () => {
                modal.confirm({
                  title: 'Retry Transaction',
                  content: 'Are you sure you want to retry this transaction?',
                  onOk: async () => {
                    try {
                      await transactionQueueService.retryTransaction(record.id);
                      message.success('Transaction queued for retry');
                      refetch();
                      queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to retry transaction';
                      message.error(errorMessage);
                    }
                  },
                });
              },
            });
          }

          if (record.status === TransactionQueueStatus.APPROVED || record.status === TransactionQueueStatus.GENERATED) {
            items.push({
              key: 'cancel',
              label: 'Cancel',
              danger: true,
              onClick: () => {
                modal.confirm({
                  title: 'Cancel Transaction',
                  content: 'Are you sure you want to cancel this transaction?',
                  onOk: async () => {
                    try {
                      await transactionQueueService.cancelTransaction(record.id, 'Manually cancelled by staff');
                      message.success('Transaction cancelled');
                      refetch();
                      queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
                    } catch (error) {
                      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel transaction';
                      message.error(errorMessage);
                    }
                  },
                });
              },
            });
          }

          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <Button icon={<MoreOutlined />} size="small" />
            </Dropdown>
          );
        },
      },
    ],
    [modal, message, refetch, queryClient],
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as string[]);
    },
    getCheckboxProps: (record: TransactionQueueWithUser) => ({
      disabled: record.status !== TransactionQueueStatus.PENDING_APPROVAL,
    }),
  };

  const handleStatusFilterChange = (value: string[]) => {
    setFilters(prev => ({ ...prev, status: value as TransactionQueueStatus[] }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDateRangeChange = (dates: null | [Dayjs | null, Dayjs | null]) => {
    if (dates && dates[0] && dates[1]) {
      const startDate = dates[0];
      const endDate = dates[1];
      if (startDate && endDate) {
        setFilters(prev => ({
          ...prev,
          fromDate: startDate.format('YYYY-MM-DD'),
          toDate: endDate.format('YYYY-MM-DD'),
        }));
      }
    } else {
      setFilters(prev => {
        const { fromDate: _fromDate, toDate: _toDate, ...rest } = prev;
        return rest;
      });
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Zahlungen prüfen, freigeben und nachverfolgen.</Typography.Text>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setGenerateModalVisible(true)}>
            Generate Transactions
          </Button>
        </Space>
      </div>

      <>
        <TransactionStats stats={stats || null} loading={statsLoading} />

        <Card>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <Tabs.TabPane tab="Transaction Queue" key="queue">
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Filters */}
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} xl={6}>
                    <Select
                      mode="multiple"
                      placeholder="Filter by status"
                      style={{ width: '100%' }}
                      onChange={handleStatusFilterChange}
                      value={filters.status as string[]}
                    >
                      {Object.values(TransactionQueueStatus).map(status => (
                        <Select.Option key={status} value={status}>
                          {status}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={12} xl={8}>
                    <RangePicker style={{ width: '100%' }} onChange={handleDateRangeChange} format="YYYY-MM-DD" />
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <Input
                      placeholder="Search (ID/user/recipient/purpose/IBAN)"
                      value={userIdSearch}
                      onChange={e => setUserIdSearch(e.target.value)}
                    />
                  </Col>
                  <Col xs={24} md={12} xl={4}>
                    <Button
                      onClick={() => {
                        setFilters({});
                        setUserIdSearch('');
                        setPagination({ page: 1, limit: 20 });
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Col>
                </Row>

                {/* Actions */}
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleBulkApprove}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Bulk Approve ({selectedRowKeys.length})
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={handleBulkReject}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Bulk Reject ({selectedRowKeys.length})
                  </Button>
                  <Button icon={<FileExcelOutlined />} onClick={handleExportCSV}>
                    Export CSV {filteredTransactions.length > 0 && `(${filteredTransactions.length})`}
                  </Button>
                </Space>

                {/* Table */}
                <Table
                  rowSelection={rowSelection}
                  columns={columns}
                  dataSource={filteredTransactions}
                  rowKey="id"
                  loading={transactionsLoading}
                  pagination={{
                    current: pagination.page,
                    pageSize: pagination.limit,
                    total: transactionsResponse?.total ?? 0,
                    showSizeChanger: true,
                    showTotal: total => `Total ${total} transactions`,
                    onChange: (page, pageSize) => setPagination({ page, limit: pageSize }),
                  }}
                  tableLayout="fixed"
                  scroll={{ x: 1040 }}
                />
              </Space>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Standing Orders" key="standing">
              <StandingOrdersTab />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Auto-Processing" key="auto-generation">
              <AutoGenerationTab />
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </>

      <TransactionDetailModal
        visible={detailModalVisible}
        transaction={selectedTransaction}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedTransaction(null);
        }}
        onActionComplete={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
        }}
      />

      <GenerateTransactionsModal
        visible={generateModalVisible}
        onClose={() => setGenerateModalVisible(false)}
        onSuccess={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ['transaction-stats'] });
        }}
      />
    </>
  );
}
